import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Mail, Lock, User, RefreshCw, ArrowLeft, ShieldCheck, Briefcase, Phone, MapPin, UserPlus, ArrowRight, Globe, Eye, EyeOff, Check, X, Building, Calendar } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useTranslation } from 'react-i18next';
import { ThreeBackground } from '../components/ThreeBackground';
import { TiltCard } from '../components/TiltCard';
import { Input } from '../components/ui/Input';
import { Button } from '../components/ui/Button';
import { motion, AnimatePresence } from 'framer-motion';

const Register = () => {
  const navigate = useNavigate();
  const { register } = useAuth();
  const { t, i18n } = useTranslation();

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    confirmPassword: '',
    role: 'candidate',
    company_name: '',
    address: '',
    phone: '',
    nationality: '',
    date_of_birth: '',
    qualification: '',
    agreeToTerms: false
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [registeredEmail, setRegisteredEmail] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  // CAPTCHA state
  const [captchaQuestion, setCaptchaQuestion] = useState({ num1: 0, num2: 0, answer: 0 });
  const [captchaAnswer, setCaptchaAnswer] = useState('');
  const [captchaError, setCaptchaError] = useState('');

  const generateCaptcha = () => {
    const num1 = Math.floor(Math.random() * 10) + 1;
    const num2 = Math.floor(Math.random() * 10) + 1;
    setCaptchaQuestion({ num1, num2, answer: num1 + num2 });
    setCaptchaAnswer('');
    setCaptchaError('');
  };

  useEffect(() => {
    generateCaptcha();
  }, []);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  // Password strength calculation
  const getPasswordStrength = (password) => {
    let strength = 0;
    if (password.length >= 8) strength++;
    if (password.length >= 12) strength++;
    if (/[a-z]/.test(password) && /[A-Z]/.test(password)) strength++;
    if (/\d/.test(password)) strength++;
    if (/[^a-zA-Z\d]/.test(password)) strength++;
    return strength;
  };

  const passwordStrength = getPasswordStrength(formData.password);
  const passwordChecks = [
    { label: 'At least 8 characters', met: formData.password.length >= 8 },
    {
      label: 'Contains uppercase & lowercase',
      met: /[a-z]/.test(formData.password) && /[A-Z]/.test(formData.password),
    },
    { label: 'Contains a number', met: /\d/.test(formData.password) },
    {
      label: 'Contains special character',
      met: /[^a-zA-Z\d]/.test(formData.password),
    },
  ];

  const getStrengthColor = () => {
    if (passwordStrength <= 1) return 'bg-red-500';
    if (passwordStrength <= 3) return 'bg-yellow-500';
    return 'bg-green-500';
  };

  const getStrengthLabel = () => {
    if (passwordStrength <= 1) return 'Weak';
    if (passwordStrength <= 3) return 'Medium';
    return 'Strong';
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess(false);
    setCaptchaError('');

    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (parseInt(captchaAnswer) !== captchaQuestion.answer) {
      setCaptchaError('CAPTCHA answer is incorrect. Please try again.');
      generateCaptcha();
      return;
    }

    if (!formData.agreeToTerms) {
      setError('You must agree to the Terms of Service and Privacy Policy.');
      return;
    }

    setLoading(true);
    const result = await register({
      name: formData.name,
      email: formData.email,
      password: formData.password,
      role: formData.role,
      company_name: formData.company_name,
      address: formData.address,
      phone: formData.phone,
      nationality: formData.nationality,
      date_of_birth: formData.date_of_birth,
      qualification: formData.qualification
    });
    setLoading(false);

    if (result.success) {
      setSuccess(true);
      setRegisteredEmail(formData.email);
      setFormData({
        name: '', email: '', password: '', confirmPassword: '',
        role: 'candidate', company_name: '', address: '', phone: '',
        nationality: '', date_of_birth: '', qualification: '', agreeToTerms: false
      });
      generateCaptcha();
    } else {
      setError(result.error);
    }
  };

  if (success) {
    return (
      <div className="min-h-screen flex items-center justify-center relative overflow-hidden bg-space-blue py-12">
        <ThreeBackground />
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-md mx-auto">
            <TiltCard>
              <div className="bg-slate-900/80 backdrop-blur-xl border border-slate-700 p-8 rounded-3xl shadow-2xl">
                <div className="bg-emerald-500/10 border border-emerald-500/20 rounded-2xl p-8 text-center">
                  <div className="w-20 h-20 bg-emerald-500 rounded-2xl flex items-center justify-center text-white mx-auto mb-6 shadow-lg shadow-emerald-500/20 relative">
                    <div className="absolute inset-0 bg-emerald-400 blur-xl opacity-20" />
                    <ShieldCheck className="w-10 h-10 relative z-10" />
                  </div>
                  <h3 className="text-2xl font-black text-white mb-4 tracking-tight">Check Your Email</h3>
                  <p className="text-slate-400 mb-8 leading-relaxed font-medium">
                    Your account (<strong className="text-white">{registeredEmail}</strong>) is now pending approval.
                    An administrator will review your application shortly.
                  </p>
                  <Button
                    onClick={() => navigate('/login')}
                    variant="primary"
                    className="w-full"
                    size="lg"
                    rightIcon={<ArrowRight className="w-5 h-5" />}
                  >
                    Back to Login
                  </Button>
                </div>
              </div>
            </TiltCard>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center relative overflow-hidden bg-space-blue py-12">
      <ThreeBackground />

      <div className="container mx-auto px-4 relative z-10">
        <div className="max-w-2xl mx-auto">
          <div className="bg-slate-900/80 backdrop-blur-xl border border-slate-700 p-8 rounded-3xl shadow-2xl">
            <div className="text-center mb-8">
              <Link to="/" className="inline-block mb-6">
                <div className="text-center">
                  <span className="text-2xl font-black text-white tracking-tight block">
                    Poly<span className="text-cyan-500">glots</span>
                  </span>
                  <span className="text-sm font-bold text-slate-400 tracking-[0.2em] uppercase">
                    Talent Portal
                  </span>
                </div>
              </Link>
              <h1 className="text-2xl font-bold text-white mb-2">
                Create Account
              </h1>
              <p className="text-slate-400">
                AI-Powered. Human-Led. Global Careers.
              </p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-5">
              {error && (
                <motion.div
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  className="p-4 bg-red-500/10 border border-red-500/20 text-red-400 rounded-xl text-sm font-bold flex items-center gap-3"
                >
                  <div className="w-1.5 h-1.5 bg-red-500 rounded-full animate-pulse" />
                  {error}
                </motion.div>
              )}

              {/* Role Selector */}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">
                <button
                  type="button"
                  onClick={() => setFormData({ ...formData, role: 'candidate' })}
                  className={`p-4 rounded-xl border-2 transition-all duration-300 flex flex-col items-center gap-2 ${formData.role === 'candidate'
                    ? 'border-cyan-500 bg-cyan-500/10 text-white shadow-[0_0_20px_rgba(6,182,212,0.2)]'
                    : 'border-slate-700 bg-slate-800/50 text-slate-400 hover:border-slate-600'
                    }`}
                >
                  <User className={`w-6 h-6 ${formData.role === 'candidate' ? 'text-cyan-400' : 'text-slate-500'}`} />
                  <span className="font-bold text-sm">I'm a Talent</span>
                </button>
                <button
                  type="button"
                  onClick={() => setFormData({ ...formData, role: 'recruiting_partner' })}
                  className={`p-4 rounded-xl border-2 transition-all duration-300 flex flex-col items-center gap-2 ${formData.role === 'recruiting_partner'
                    ? 'border-cyan-500 bg-cyan-500/10 text-white shadow-[0_0_20px_rgba(6,182,212,0.2)]'
                    : 'border-slate-700 bg-slate-800/50 text-slate-400 hover:border-slate-600'
                    }`}
                >
                  <Building className={`w-6 h-6 ${formData.role === 'recruiting_partner' ? 'text-cyan-400' : 'text-slate-500'}`} />
                  <span className="font-bold text-sm">I'm an Employer</span>
                </button>
                <button
                  type="button"
                  onClick={() => setFormData({ ...formData, role: 'sourcing_partner' })}
                  className={`p-4 rounded-xl border-2 transition-all duration-300 flex flex-col items-center gap-2 ${formData.role === 'sourcing_partner'
                    ? 'border-cyan-500 bg-cyan-500/10 text-white shadow-[0_0_20px_rgba(6,182,212,0.2)]'
                    : 'border-slate-700 bg-slate-800/50 text-slate-400 hover:border-slate-600'
                    }`}
                >
                  <UserPlus className={`w-6 h-6 ${formData.role === 'sourcing_partner' ? 'text-cyan-400' : 'text-slate-500'}`} />
                  <span className="font-bold text-sm">I'm an Agency</span>
                </button>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <Input
                  label="Full Name"
                  name="name"
                  required
                  value={formData.name}
                  onChange={handleChange}
                  placeholder="John Doe"
                  icon={<User className="w-5 h-5" />}
                />
                <Input
                  label="Email Address"
                  name="email"
                  type="email"
                  required
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="you@example.com"
                  icon={<Mail className="w-5 h-5" />}
                />
              </div>

              <AnimatePresence mode="wait">
                {/* Partner Fields */}
                {(formData.role === 'recruiting_partner' || formData.role === 'sourcing_partner') && (
                  <motion.div
                    key="partner-fields"
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                    className="space-y-6"
                  >
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <Input
                        label="Phone Number"
                        name="phone"
                        type="tel"
                        required
                        value={formData.phone}
                        onChange={handleChange}
                        placeholder="+49 123 45678"
                        icon={<Phone className="w-5 h-5" />}
                      />
                      <Input
                        label="Company Name"
                        name="company_name"
                        required
                        value={formData.company_name}
                        onChange={handleChange}
                        placeholder="Agency Name"
                        icon={<Briefcase className="w-5 h-5" />}
                      />
                    </div>
                    <div className="space-y-1.5">
                      <label className="block text-sm font-medium text-slate-300 ml-1">
                        Business Address
                      </label>
                      <div className="relative group">
                        <MapPin className="absolute left-3 top-4 text-slate-500 group-focus-within:text-cyan-400 transition-colors pointer-events-none" />
                        <textarea
                          name="address"
                          required
                          value={formData.address}
                          onChange={handleChange}
                          rows={2}
                          className="w-full bg-slate-800/50 border-2 border-slate-700 rounded-xl pl-10 pr-4 py-3 text-white focus:bg-slate-800 focus:border-cyan-500 focus:outline-none transition-all font-medium"
                          placeholder="Street, City, Postcode"
                        />
                      </div>
                    </div>
                  </motion.div>
                )}

                {/* Candidate Fields - PRESERVED */}
                {formData.role === 'candidate' && (
                  <motion.div
                    key="candidate-fields"
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                    className="space-y-6"
                  >
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                      <Input
                        label="Nationality"
                        name="nationality"
                        required
                        value={formData.nationality}
                        onChange={handleChange}
                        placeholder="e.g. Indian"
                        icon={<Globe className="w-5 h-5" />}
                      />
                      <Input
                        label="Date of Birth"
                        name="date_of_birth"
                        type="date"
                        required
                        value={formData.date_of_birth}
                        onChange={handleChange}
                        icon={<Calendar className="w-5 h-5" />}
                      />
                      <Input
                        label="Education"
                        name="qualification"
                        required
                        value={formData.qualification}
                        onChange={handleChange}
                        placeholder="B.Sc Nursing"
                        icon={<RefreshCw className="w-5 h-5" />}
                      />
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="relative">
                  <Input
                    label="Password"
                    name="password"
                    type={showPassword ? 'text' : 'password'}
                    required
                    value={formData.password}
                    onChange={handleChange}
                    placeholder="••••••••"
                    icon={<Lock className="w-5 h-5" />}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-[42px] text-slate-400 hover:text-cyan-400 transition-colors"
                  >
                    {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
                <div className="relative">
                  <Input
                    label="Confirm Password"
                    name="confirmPassword"
                    type={showConfirmPassword ? 'text' : 'password'}
                    required
                    value={formData.confirmPassword}
                    onChange={handleChange}
                    placeholder="••••••••"
                    icon={<Lock className="w-5 h-5" />}
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    className="absolute right-3 top-[42px] text-slate-400 hover:text-cyan-400 transition-colors"
                  >
                    {showConfirmPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
              </div>

              {/* Password Strength Indicator */}
              {formData.password && (
                <motion.div
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="space-y-2"
                >
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-slate-400">Password strength:</span>
                    <span
                      className={`font-bold ${passwordStrength <= 1
                        ? 'text-red-400'
                        : passwordStrength <= 3
                          ? 'text-yellow-400'
                          : 'text-green-400'
                        }`}
                    >
                      {getStrengthLabel()}
                    </span>
                  </div>
                  <div className="h-1 bg-slate-700 rounded-full overflow-hidden">
                    <motion.div
                      initial={{ width: 0 }}
                      animate={{ width: `${(passwordStrength / 5) * 100}%` }}
                      className={`h-full ${getStrengthColor()} transition-all duration-300`}
                    />
                  </div>

                  <div className="space-y-1 text-xs">
                    {passwordChecks.map((check, i) => (
                      <div
                        key={i}
                        className={`flex items-center gap-2 ${check.met ? 'text-green-400' : 'text-slate-500'
                          }`}
                      >
                        {check.met ? <Check className="w-3 h-3" /> : <X className="w-3 h-3" />}
                        <span>{check.label}</span>
                      </div>
                    ))}
                  </div>
                </motion.div>
              )}

              {/* Terms and Conditions Checkbox */}
              <div className="flex items-start gap-2">
                <input
                  type="checkbox"
                  id="agreeToTerms"
                  checked={formData.agreeToTerms || false}
                  onChange={(e) => setFormData({ ...formData, agreeToTerms: e.target.checked })}
                  className="mt-1 rounded border-slate-600 bg-slate-800 text-cyan-500 focus:ring-cyan-500"
                />
                <label htmlFor="agreeToTerms" className="text-sm text-slate-400">
                  I agree to the{' '}
                  <Link to="/terms" className="text-cyan-400 hover:text-cyan-300">
                    Terms of Service
                  </Link>{' '}
                  and{' '}
                  <Link to="/privacy" className="text-cyan-400 hover:text-cyan-300">
                    Privacy Policy
                  </Link>
                </label>
              </div>

              <div className="space-y-3">
                <label className="block text-xs font-bold text-slate-300 ml-1 uppercase tracking-widest opacity-70">
                  Security Protocol
                </label>
                <div className="bg-slate-900/40 rounded-xl p-4 border border-white/5 relative overflow-hidden">
                  <div className="absolute top-0 right-0 w-24 h-24 bg-cyan-500/5 blur-2xl rounded-full" />
                  <div className="flex items-center gap-4 relative z-10">
                    <div className="bg-[#020617] border-2 border-slate-800 rounded-lg px-4 py-2 font-black text-lg text-white tracking-wide shadow-inner flex-1 text-center border-cyan-400/20">
                      {captchaQuestion.num1} + {captchaQuestion.num2} = ?
                    </div>
                    <div className="w-20">
                      <input
                        type="number"
                        required
                        value={captchaAnswer}
                        onChange={(e) => setCaptchaAnswer(e.target.value)}
                        className="w-full bg-slate-900 border-2 border-slate-700 rounded-lg px-3 py-2 font-black text-lg text-center text-white focus:border-cyan-400 focus:outline-none transition-all shadow-[0_0_20px_rgba(0,0,0,0.5)]"
                        placeholder="#"
                      />
                    </div>
                    <button
                      type="button"
                      onClick={generateCaptcha}
                      className="p-3 bg-slate-800 border-2 border-slate-700 rounded-lg text-slate-500 hover:text-cyan-400 hover:border-cyan-400 transition-all shadow-lg active:scale-95 group"
                    >
                      <RefreshCw className="w-5 h-5 group-hover:rotate-180 transition-transform duration-500" />
                    </button>
                  </div>
                </div>
                {captchaError && (
                  <p className="mt-2 text-sm font-bold text-red-400 text-center flex items-center justify-center gap-2">
                    <span className="w-1.5 h-1.5 bg-red-400 rounded-full animate-pulse" />
                    {captchaError}
                  </p>
                )}
              </div>

              <Button
                type="submit"
                disabled={loading}
                className="w-full"
                size="lg"
                rightIcon={!loading && <ArrowRight className="w-5 h-5" />}
              >
                {loading ? 'Creating Account...' : 'Create Account'}
              </Button>

              <p className="text-center text-sm text-slate-400 font-medium">
                Already have an account?{' '}
                <Link to="/login" className="text-cyan-400 hover:text-cyan-300 font-bold tracking-tight transition-colors">
                  Sign in
                </Link>
              </p>

              <Link
                to="/"
                className="flex items-center justify-center text-slate-500 hover:text-slate-300 text-sm font-black uppercase tracking-widest transition-colors group"
              >
                <ArrowLeft className="w-4 h-4 mr-2 group-hover:-translate-x-1 transition-transform" />
                Back to Home
              </Link>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Register;
